import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './ChecklistModal.css';

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  try {
    // Tomar solo la parte de la fecha antes de la T
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0].slice(2)}`;
  } catch { return dateStr; }
}

export default function ChecklistModal({ event, canOperate, onClose, onOpenChecklist }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);

  useEffect(() => {
    api.get(`/checklists/event/${event.id}`)
      .then(r => setChecklists(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [event.id]);

  const getOrCreate = async (type) => {
    setCreating(type);
    try {
      const existing = checklists.find(c => c.type === type);
      if (existing) {
        if (type === 'DESCARGA') {
          await api.post(`/checklists/${existing.id}/sync-from-carga`);
        }
        onOpenChecklist(existing.id);
        return;
      }
      const { data } = await api.post('/checklists', { event_id: event.id, type });
      onOpenChecklist(data.id);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setCreating(null);
    }
  };

  const staffEntries = [
    { label: 'Armado',    value: event.team_setup },
    { label: 'Operación', value: event.team_operation },
    { label: 'Desarmado', value: event.team_breakdown },
  ].filter(e => e.value);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal event-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{event.name}</h2>
            <div className="event-meta">
              {event.event_date && (
                <span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {' '}{formatEventDate(event.event_date)}
                </span>
              )}
              {event.start_time && (
                <span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {' '}{event.start_time}{event.end_time ? ` – ${event.end_time}hs` : 'hs'}
                </span>
              )}
              {event.assigned_people && (
                <span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  {' '}{event.assigned_people}
                </span>
              )}
            </div>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Staff section */}
        {staffEntries.length > 0 && (
          <div className="equipment-preview">
            <div className="ep-header">
              <span className="ep-title">Personal asignado</span>
            </div>
            <div className="ep-groups">
              {staffEntries.map((entry, i) => (
                <div key={i} className="ep-group">
                  <span className="ep-zone">{entry.label}</span>
                  <span className="ep-items" style={{ whiteSpace: 'pre-line' }}>{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Checklist actions */}
        {canOperate && (
          <div className="checklist-actions">
            <div className="ca-title">Checklists operativos</div>
            {loading ? (
              <div className="text-dim text-sm">Cargando...</div>
            ) : (
              <div className="ca-buttons">
                {['CARGA', 'DESCARGA'].map(type => {
                  const existing = checklists.find(c => c.type === type);
                  return (
                    <button
                      key={type}
                      className={`ca-btn ${existing ? 'exists' : ''}`}
                      onClick={() => getOrCreate(type)}
                      disabled={creating === type}
                    >
                      <span className="ca-btn-icon">
                        {type === 'CARGA' ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        )}
                      </span>
                      <span className="ca-btn-label">{type}</span>
                      <span className="ca-btn-status">
                        {creating === type
                          ? 'Abriendo...'
                          : existing
                            ? (
                              <>
                                {existing.status === 'closed' ? (
                                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>{' Cerrado'}</>
                                ) : (
                                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="9 11 12 14 22 4"/></svg>{' Activo'}</>
                                )}
                                {' · Ver'}
                              </>
                            )
                            : 'Iniciar'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!canOperate && (
          <div className="text-dim text-sm mt-16">
            Solo usuarios de depósito o administradores pueden operar checklists.
          </div>
        )}
      </div>
    </div>
  );
}
